// `/app/withdrawals/$id` (croqui `screens/seller/saques.tsx`, frame `DetalheSaque`).
//
// Este componente é o CONTEÚDO do `RouteDrawer` da rota-pai: o cabeçalho (título, id e o
// botão de fechar) é do drawer, e a lista continua desenhada embaixo, inclusive em deep
// link.
//
// Cancelar passa pelo `StepUpDialog` de `api/client.ts` quando a API pedir o segundo
// fator (`POST /v1/me/withdrawals/{id}/cancel` tem `mfaStepUp`).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { cancelWithdrawal, withdrawalQueryOptions } from '../api/queries/withdrawals.ts';
import { useMode } from '../app/mode.tsx';
import { EmptyState } from '../components/app/empty-state.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Wallet } from '../components/icons/index.tsx';
import { isNotFound } from '../features/withdrawals/errors.ts';
import { WithdrawalDetail } from '../features/withdrawals/withdrawal-detail.tsx';

function WithdrawalPage() {
  const { id } = Route.useParams();
  const { mode } = useMode();
  const queryClient = useQueryClient();

  const detail = useQuery(withdrawalQueryOptions(mode, id));

  const cancel = useMutation({
    mutationFn: () => cancelWithdrawal(mode, id),
    onSuccess: async (withdrawal) => {
      queryClient.setQueryData(withdrawalQueryOptions(mode, id).queryKey, withdrawal);
      await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      await queryClient.invalidateQueries({ queryKey: ['balance'] });
      await queryClient.invalidateQueries({ queryKey: ['counters'] });
    },
  });

  if (detail.isPending) {
    return <CardSkeleton lines={4} />;
  }

  // 404 pelo `code`, nunca pelo texto do status (`spec/17` §4.3 item 1).
  if (isNotFound(detail.error)) {
    return (
      <EmptyState
        bare
        icon={Wallet}
        title="Saque não encontrado"
        description="Este saque não existe nesta conta ou está em outro modo (live/test)."
      />
    );
  }

  if (detail.error !== null || detail.data === undefined) {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  return (
    <WithdrawalDetail
      withdrawal={detail.data}
      cancelling={cancel.isPending}
      cancelError={cancel.error}
      onCancel={() => {
        cancel.mutate();
      }}
    />
  );
}

export const Route = createFileRoute('/app/withdrawals/$id')({ component: WithdrawalPage });
